import { FC, useCallback, useState } from 'react';
import { TagIcon, UsersThreeIcon } from '@phosphor-icons/react';
import CustomTabsV3 from '../../shared/CustomTabsV3/CustomTabsV3';
import CustomButton from '../../shared/CustomButton/CustomButton';
import EmptyContainer from '../../shared/EmptyContainer/EmptyContainer';
import { useAllenamentiPage, useResolvedTeamId } from './useAllenamentiPage';
import { useAllenamentiTaxonomy } from './hooks/useAllenamentiTaxonomy';
import { useAllenamentiWeek } from './week/useAllenamentiWeek';
import { useTeamDailyLoadRecovery } from './week/useTeamDailyLoadRecovery';
import WeekBoard from './week/WeekBoard';
import BlockFormModal from './week/BlockFormModal';
import CategoriesDrawer from './week/CategoriesDrawer';
import { BlockClipboardProvider } from './week/BlockClipboardProvider';
import { useBlockClipboard } from './week/useBlockClipboard';
import AnalysisView from './analysis/AnalysisView';
import styles from './AllenamentiPage.module.scss';

interface IAllenamentiPageProps {
  /** Passato quando la pagina è incorporata nel dettaglio squadra. */
  teamId?: string;
}

/**
 * Modulo Allenamenti: pianificazione settimanale del lavoro di una squadra,
 * più la vista Analisi (M3) — composizione dello stesso lavoro su
 * settimana/mese/stagione. Due modi della stessa pagina, non due route: la
 * vista Analisi vive dentro il tab `allenamenti` già montato in TeamDetail
 * (design M2/M3 §5.1 — niente terza pagina orfana come
 * TrainingStatisticsDashboard/WeeklyReports).
 *
 * I minuti inseriti qui sono pianificati e di squadra: non vanno confrontati
 * con lo sforzo percepito dei singoli atleti né con i dati GPS.
 *
 * Il guscio esterno esiste solo per montare `BlockClipboardProvider` SOPRA il
 * resto (F1 copia/incolla): un componente non può consumare un Context che
 * lui stesso fornisce nel proprio JSX, va per forza un livello più in alto.
 *
 * `key={teamId}`: la route `teams/:teamId/allenamenti` riusa la STESSA
 * istanza di `AllenamentiPage` quando si naviga da una squadra all'altra
 * (React Router non rimonta un elemento solo perché cambia il parametro), e
 * il Context di `BlockClipboardProvider` non ha altrimenti modo di accorgersi
 * del cambio — un blocco copiato dalla squadra A resterebbe incollabile
 * nella B. Cambiare `key` forza React a scartare l'istanza vecchia (appunti
 * compresi) e montarne una nuova pulita.
 */
const AllenamentiPage: FC<IAllenamentiPageProps> = (props) => {
  const teamId = useResolvedTeamId(props.teamId);
  return (
    <BlockClipboardProvider key={teamId}>
      <AllenamentiPageContent {...props} />
    </BlockClipboardProvider>
  );
};

const AllenamentiPageContent: FC<IAllenamentiPageProps> = ({ teamId: teamIdProp }) => {
  const {
    teamId,
    view,
    setView,
    isModalOpen,
    editingBlock,
    modalDate,
    openCreate,
    openEdit,
    closeModal,
  } = useAllenamentiPage(teamIdProp);

  const [isCategoriesOpen, setCategoriesOpen] = useState(false);
  const { categories } = useAllenamentiTaxonomy();

  const {
    weekLabel,
    weekDays,
    blocks,
    blocksByDay,
    totalMinutes,
    totalBlocks,
    trainingDays,
    dominantCategory,
    isLoading,
    isError,
    error,
    refetch,
    goPrevWeek,
    goNextWeek,
    createBlock,
    updateBlock,
    reorderBlocks,
    removeBlock,
    isSaving,
    isCreatingBlock,
  } = useAllenamentiWeek(teamId);

  // RPE medio squadra + recupero medio squadra per giorno (dato di
  // giorno+squadra, non del singolo blocco): fetch solo quando la vista
  // settimana è quella attiva, sulla stessa settimana lun-dom già mostrata.
  const dailyLoadByDate = useTeamDailyLoadRecovery(
    teamId,
    weekDays[0]?.iso ?? '',
    view === 'settimana',
  );

  const { clipboard } = useBlockClipboard();

  /**
   * Incolla (F1): crea il blocco in fondo al giorno target con
   * `createBlock`, la stessa mutation del form "Aggiungi blocco" — nessun
   * endpoint nuovo. L'`order` è il MASSIMO fra quelli già presenti nel giorno
   * + 1 (non la lunghezza dell'array): dopo la cancellazione di un blocco
   * centrale gli `order` restanti non sono più contigui da 0, e la lunghezza
   * ricadrebbe in un valore già occupato in mezzo alla colonna invece che in
   * fondo. Giorno vuoto → `-1 + 1 = 0`, invariato. Se una categoria copiata è
   * stata nel frattempo disattivata, il backend rifiuta la creazione
   * (assertValid risolve solo categorie attive): stesso comportamento di
   * qualunque altra scrittura, coperto dal toast di errore già cablato in
   * createMutation — nessun caso speciale da gestire qui.
   */
  const handlePasteBlock = useCallback(
    (iso: string) => {
      if (!clipboard) return; // il bottone non compare mai a clipboard vuota
      const dayBlocks = blocksByDay.get(iso) ?? [];
      const order = Math.max(-1, ...dayBlocks.map((b) => b.order)) + 1;
      createBlock({ ...clipboard, teamId, localDate: iso, order }).catch(() => {});
    },
    [clipboard, blocksByDay, teamId, createBlock],
  );

  if (!teamId) {
    return (
      <EmptyContainer
        Icon={UsersThreeIcon}
        title="Nessuna squadra selezionata"
        description="Scegli una squadra per pianificarne gli allenamenti."
        addButton={false}
        onClick={() => {}}
      />
    );
  }

  return (
    <div className={styles.page}>
      {/* Le due viste sono modi della stessa pagina, e le tab del progetto
          sono già quelle: `CustomTabsV3` invece di due <button> a mano, come
          in UsersPage, CalendarPage e GroupAthletes. Il contenuto resta fuori
          da `Tabs.Content` perché la board vive a tutta larghezza. */}
      <div className={styles.viewRow}>
        <CustomTabsV3
          tabs={[
            { label: 'Settimana', value: 'settimana' },
            { label: 'Analisi', value: 'analisi' },
          ]}
          value={view}
          onValueChange={(e) => setView(e.value as typeof view)}
          className={styles.viewSwitch}
        />
        <CustomButton
          size="sm"
          color="text"
          icon={<TagIcon size={16} />}
          iconPosition="left"
          onClick={() => setCategoriesOpen(true)}
        >
          Categorie
        </CustomButton>
      </div>

      {view === 'settimana' ? (
        <WeekBoard
          weekLabel={weekLabel}
          weekDays={weekDays}
          blocks={blocks}
          blocksByDay={blocksByDay}
          totalMinutes={totalMinutes}
          totalBlocks={totalBlocks}
          trainingDays={trainingDays}
          dominantCategory={dominantCategory}
          dailyLoadByDate={dailyLoadByDate}
          isLoading={isLoading}
          isError={isError}
          error={error}
          onPrevWeek={goPrevWeek}
          onNextWeek={goNextWeek}
          onAddBlock={openCreate}
          onEditBlock={openEdit}
          onPasteBlock={handlePasteBlock}
          isPastingBlock={isCreatingBlock}
          onRetry={refetch}
          onReorder={({ days }) => {
            // Fire-and-forget: l'aggiornamento ottimistico e il rollback con
            // toast di errore vivono già dentro reorderMutation.
            reorderBlocks({ teamId, days }).catch(() => {});
          }}
        />
      ) : (
        <AnalysisView teamId={teamId} />
      )}

      <BlockFormModal
        isOpen={isModalOpen}
        teamId={teamId}
        defaultDate={modalDate}
        editing={editingBlock}
        isSaving={isSaving}
        onClose={closeModal}
        onCreate={createBlock}
        onUpdate={(id, body) => updateBlock({ id, body })}
        onDelete={removeBlock}
      />

      <CategoriesDrawer
        isOpen={isCategoriesOpen}
        categories={categories}
        onClose={() => setCategoriesOpen(false)}
      />
    </div>
  );
};

export default AllenamentiPage;
